/**
 * Type definitions for Transfer/No-Transfer (T/NT) outcomes metadata
 * Stored in CustomFieldValue.metadata field
 */

export type TransferOutcome = 'transfer' | 'not-transfer';
export type IntentType = 'schedule' | 'reschedule' | 'confirm' | 'cancel';

// Practice/Provider level outcomes - supports both old (string) and new (object) format
export type PracticeProviderLevelOutcome =
  | TransferOutcome // Old format: single value
  | {
      // New format: object with all intents
      schedule: TransferOutcome;
      reschedule: TransferOutcome;
      confirm: TransferOutcome;
      cancel: TransferOutcome;
    };

export interface AppointmentTypeOutcome {
  reschedule: TransferOutcome;
  schedule: TransferOutcome;
  confirm?: TransferOutcome; // Optional for backwards compatibility
  cancel?: TransferOutcome; // Optional for backwards compatibility
}

// Specialty outcomes - supports both old (string) and new (object) format
export type SpecialtyOutcome =
  | TransferOutcome // Old format: single value
  | {
      // New format: object with all intents
      schedule: TransferOutcome;
      reschedule: TransferOutcome;
      confirm: TransferOutcome;
      cancel: TransferOutcome;
    };

export interface TransferOutcomesData {
  // Flag for bot to always transfer (independent of configurable outcomes)
  // Set to true when "Save Alert" is clicked, false when "Approve Transfer Outcomes" is clicked
  alwaysTransfer: boolean;

  // Practice/Provider level outcome
  // Old format: single TransferOutcome value
  // New format: object with schedule, reschedule, confirm, cancel
  practiceProviderLevel: PracticeProviderLevelOutcome;

  // Appointment type outcomes (applies to both Provider and Practice level)
  // Each appointment type has separate outcomes for 'schedule', 'reschedule', 'confirm', and 'cancel'
  // Appointment types are added via dropdown - no defaults pre-populated
  appointmentTypes: {
    [appointmentType: string]: AppointmentTypeOutcome;
  };

  // Specialty outcomes (Practice level only - shown in screenshots)
  // Old format: single TransferOutcome value per specialty
  // New format: object with schedule, reschedule, confirm, cancel per specialty
  specialties?: {
    [specialty: string]: SpecialtyOutcome;
  };
}

export interface CommentLogEntry {
  // User information
  userId: string; // User ID (from JWT sub or customer_id)
  userName: string; // User name (from JWT name)
  userEmail: string; // User email (from JWT email)
  permissionGroup: string; // Permission group (from JWT permission_group)

  // Action taken
  action: 'created' | 'submitted_for_approval' | 'updated' | 'approved' | 'comment_added';

  // Timestamp
  timestamp: string; // ISO 8601 timestamp

  // Optional comment text (rich text HTML)
  comment?: string;
}

export interface TransferOutcomesMetadata {
  transferOutcomes: TransferOutcomesData;

  // Audit/comment log - list of all changes and comments
  // Replaces the old rationale field
  commentLog?: CommentLogEntry[];

  // Approval workflow status
  approvalStatus?: 'draft' | 'pending' | 'approved';

  // Approval tracking (kept for backward compatibility, but commentLog is the source of truth)
  submittedForApproval?: boolean;
  submittedAt?: string; // ISO 8601 timestamp
  submittedBy?: string; // User ID or email

  approvedBy?: string; // User ID or email (CHS admin)
  approvedAt?: string; // ISO 8601 timestamp
}

// Helper function to create default transfer outcomes structure
export function createDefaultTransferOutcomes(): TransferOutcomesData {
  return {
    alwaysTransfer: true, // Default to true (always transfer until approved)
    practiceProviderLevel: {
      schedule: 'transfer',
      reschedule: 'transfer',
      confirm: 'not-transfer',
      cancel: 'not-transfer',
    },
    appointmentTypes: {}, // Start with empty - users add appointment types via dropdown
    specialties: {}, // Practice level only
  };
}

/**
 * Normalize practice/provider level outcome to new format
 * Handles backwards compatibility: old string format -> new object format
 */
export function normalizePracticeProviderLevel(
  practiceProviderLevel: PracticeProviderLevelOutcome
): {
  schedule: TransferOutcome;
  reschedule: TransferOutcome;
  confirm: TransferOutcome;
  cancel: TransferOutcome;
} {
  if (typeof practiceProviderLevel === 'string') {
    // Old format: single value applies to schedule and reschedule
    // confirm and cancel default to 'not-transfer'
    return {
      schedule: practiceProviderLevel,
      reschedule: practiceProviderLevel,
      confirm: 'not-transfer',
      cancel: 'not-transfer',
    };
  }
  // New format: already an object
  return practiceProviderLevel;
}

/**
 * Normalize appointment type outcome to new format
 * Handles backwards compatibility: missing confirm/cancel -> default to 'not-transfer'
 */
export function normalizeAppointmentTypeOutcome(
  outcome: AppointmentTypeOutcome | undefined
): {
  schedule: TransferOutcome;
  reschedule: TransferOutcome;
  confirm: TransferOutcome;
  cancel: TransferOutcome;
} {
  if (!outcome) {
    return {
      schedule: 'transfer',
      reschedule: 'transfer',
      confirm: 'not-transfer',
      cancel: 'not-transfer',
    };
  }
  return {
    schedule: outcome.schedule,
    reschedule: outcome.reschedule,
    confirm: outcome.confirm ?? 'not-transfer',
    cancel: outcome.cancel ?? 'not-transfer',
  };
}

/**
 * Normalize specialty outcome to new format
 * Handles backwards compatibility: old string format -> new object format
 */
export function normalizeSpecialtyOutcome(
  specialtyOutcome: SpecialtyOutcome | undefined
): {
  schedule: TransferOutcome;
  reschedule: TransferOutcome;
  confirm: TransferOutcome;
  cancel: TransferOutcome;
} {
  if (!specialtyOutcome) {
    return {
      schedule: 'transfer',
      reschedule: 'transfer',
      confirm: 'not-transfer',
      cancel: 'not-transfer',
    };
  }
  if (typeof specialtyOutcome === 'string') {
    // Old format: single value applies to schedule and reschedule
    // confirm and cancel default to 'not-transfer'
    return {
      schedule: specialtyOutcome,
      reschedule: specialtyOutcome,
      confirm: 'not-transfer',
      cancel: 'not-transfer',
    };
  }
  // New format: already an object
  return specialtyOutcome;
}

// Helper function to check if transfer outcomes exist in metadata
export function hasTransferOutcomes(
  metadata: Record<string, unknown> | undefined
): boolean {
  return (
    metadata !== undefined &&
    typeof metadata.transferOutcomes === 'object' &&
    metadata.transferOutcomes !== null
  );
}

// Helper function to get transfer outcomes from metadata
export function getTransferOutcomes(
  metadata: Record<string, unknown> | undefined
): TransferOutcomesData | null {
  if (!hasTransferOutcomes(metadata) || !metadata) {
    return null;
  }

  const transferOutcomes = metadata.transferOutcomes as TransferOutcomesData;
  return transferOutcomes;
}

/**
 * Create a new comment log entry
 */
export function createCommentLogEntry(
  action: CommentLogEntry['action'],
  userInfo: {
    userId: string;
    userName: string;
    userEmail: string;
    permissionGroup: string;
  },
  comment?: string
): CommentLogEntry {
  return {
    userId: userInfo.userId,
    userName: userInfo.userName,
    userEmail: userInfo.userEmail,
    permissionGroup: userInfo.permissionGroup,
    action,
    timestamp: new Date().toISOString(),
    comment,
  };
}

/**
 * Get comment log from metadata, or return empty array
 */
export function getCommentLog(
  metadata: Record<string, unknown> | undefined
): CommentLogEntry[] {
  if (!metadata || !Array.isArray(metadata.commentLog)) {
    return [];
  }
  return metadata.commentLog as CommentLogEntry[];
}

